/**
 * ConnectionsCard Component
 *
 * Dashboard card for managing user connections ("friends list").
 * Shows connection count, recent connections, pending requests badge,
 * and actions to find new connections.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Link2,
  Clock,
  UserCheck,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useConnections,
  useConnectionCount,
  usePendingReceivedCount,
  useConnectionLoading,
  useConnectionActions,
} from '@/stores/connection';
import { UserSearchModal } from '@/components/connections/UserSearchModal';
import { useConversations } from '@/stores/messaging';

import type { UserConnection } from '@/types/connections';

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (connection: UserConnection): string => {
  const { user } = connection;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * Format connected date
 */
const formatConnectedDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
};

/**
 * ConnectionRow - Individual connection row
 */
interface ConnectionRowProps {
  connection: UserConnection;
  onMessageClick: () => void;
}

const ConnectionRow: React.FC<ConnectionRowProps> = ({ connection, onMessageClick }) => {
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <div className="flex items-center gap-3 py-2 group">
      <Avatar className="h-8 w-8">
        <AvatarImage src={connection.user.avatar} alt={displayName} />
        <AvatarFallback
          style={{
            backgroundColor: 'hsl(var(--primary) / 0.1)',
            color: 'hsl(var(--primary))',
          }}
          className="text-xs font-medium"
        >
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{displayName}</p>
        <p className="text-xs text-muted-foreground">@{connection.user.username}</p>
      </div>

      {/* Message button - appears on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          console.log('[ConnectionsCard] Message button clicked for:', connection.user.id);
          onMessageClick();
        }}
        className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-accent transition-all"
        aria-label={`Message ${displayName}`}
        title={`Message ${displayName}`}
      >
        <MessageSquare className="h-4 w-4 text-muted-foreground hover:text-foreground" />
      </button>

      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Clock className="h-3 w-3" />
        <span>{formatConnectedDate(connection.connectedAt)}</span>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for connections
 */
const ConnectionsSkeleton: React.FC = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3 py-2">
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-4 w-24 mb-1" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-3 w-16" />
      </div>
    ))}
  </div>
);

/**
 * ConnectionsCard - Main dashboard card component
 */
export const ConnectionsCard: React.FC = () => {
  const navigate = useNavigate();
  const connections = useConnections();
  const connectionCount = useConnectionCount();
  const pendingReceivedCount = usePendingReceivedCount();
  const { isLoading } = useConnectionLoading();
  const { fetchConnections, fetchPendingReceived } = useConnectionActions();
  const conversations = useConversations();

  const [showSearchModal, setShowSearchModal] = useState(false);

  /**
   * Handle message button click for a connection
   */
  const handleMessageClick = (userId: string) => {
    // Check if there's an existing conversation with this user
    const existingConversation = conversations.find(
      (c) => c.participant?.id === userId
    );

    if (existingConversation) {
      console.log('[ConnectionsCard] Found existing conversation:', existingConversation.id);
      navigate(`/dashboard/messages?conversation=${existingConversation.id}`);
    } else {
      // Navigate to messages page with userId to start new conversation
      console.log('[ConnectionsCard] No existing conversation, navigating to start new');
      navigate(`/dashboard/messages?newMessage=${userId}`);
    }
  };

  // Fetch connections and pending requests on mount
  useEffect(() => {
    console.log('[ConnectionsCard] Mounting, fetching connections...');
    fetchConnections();
    fetchPendingReceived();
  }, [fetchConnections, fetchPendingReceived]);

  // Get recent connections (max 5)
  const recentConnections = connections.slice(0, 5);
  const hasMoreConnections = connections.length > 5;

  console.log('[ConnectionsCard] Rendering with', connectionCount, 'connections,', pendingReceivedCount, 'pending');

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              <CardTitle>Connections</CardTitle>
              {/* Pending requests badge */}
              {pendingReceivedCount > 0 && (
                <Badge
                  variant="default"
                  className="ml-2 h-5 min-w-[20px] px-1.5 text-xs"
                  style={{
                    backgroundColor: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                  }}
                >
                  {pendingReceivedCount}
                </Badge>
              )}
            </div>

            {/* View All button */}
            <Button
              variant="ghost"
              size="sm"
              className="text-sm text-muted-foreground hover:text-foreground"
              onClick={() => navigate('/dashboard/connections')}
            >
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <CardDescription>
            Your network of connected users
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-1">
          {isLoading ? (
            <ConnectionsSkeleton />
          ) : connectionCount === 0 ? (
            /* Empty state */
            <div className="text-center py-6">
              <div
                className="h-12 w-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{
                  backgroundColor: 'hsl(var(--primary) / 0.1)',
                }}
              >
                <Users className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} />
              </div>
              <p className="text-sm font-medium mb-1">No connections yet</p>
              <p className="text-xs text-muted-foreground mb-4">
                Find and connect with other users to grow your network
              </p>
              <Button
                onClick={() => {
                  console.log('[ConnectionsCard] Opening search modal from empty state');
                  setShowSearchModal(true);
                }}
                size="sm"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Find Connections
              </Button>
            </div>
          ) : (
            /* Connections list */
            <div className="space-y-1">
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {recentConnections.map((connection) => (
                  <ConnectionRow
                    key={connection.connectionId}
                    connection={connection}
                    onMessageClick={() => handleMessageClick(connection.user.id)}
                  />
                ))}

                {hasMoreConnections && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    And {connections.length - 5} more...
                  </p>
                )}
              </div>

              {/* Pending requests indicator */}
              {pendingReceivedCount > 0 && (
                <div
                  className="flex items-center justify-between p-2 mt-3 rounded-lg"
                  style={{
                    backgroundColor: 'hsl(var(--primary) / 0.1)',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
                    <span className="text-sm font-medium">
                      {pendingReceivedCount} pending request{pendingReceivedCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      console.log('[ConnectionsCard] Navigating to pending requests');
                      window.location.href = '/dashboard/connections?tab=pending';
                    }}
                  >
                    View
                  </Button>
                </div>
              )}

              {/* Find connections button */}
              <div className="pt-3">
                <Button
                  onClick={() => {
                    console.log('[ConnectionsCard] Opening search modal');
                    setShowSearchModal(true);
                  }}
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Find Connections
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Search Modal */}
      <UserSearchModal
        open={showSearchModal}
        onOpenChange={setShowSearchModal}
      />
    </>
  );
};

export default ConnectionsCard;
